import { Button } from "@components/common/button"
import { useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "../common/popover"
import { DictType } from "@pma/types"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../common/command"
import useTranslator from "@pma/hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import { SERVER_URL, WORK_DIARY_EDIT_ENDPOINT } from "@pma/utils/api-routes"
import { toast } from "@pma/hooks/use-toast"

interface Props {
  status_id?: number
  lstStatus: any[]
  workDiaryDetails: DictType | undefined
  disabled?: boolean
}
const WorkDiaryStatusEditor = ({
  status_id,
  lstStatus,
  workDiaryDetails,
  disabled = false,
}: Props) => {
  const queryClient = useQueryClient()
  const { translate, locale } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const isHebrew = locale === "he"

  const [open, setOpen] = useState(false)

  const updateWorkDiaryStatus = async (statusId: number) => {
    const response = await axios.post(
      SERVER_URL + WORK_DIARY_EDIT_ENDPOINT,
      {
        project_id: workDiaryDetails?.project_id,
        date: workDiaryDetails?.diary_date,
        weather_id: workDiaryDetails?.weather_id,
        address: workDiaryDetails?.address,
        contractor_id: workDiaryDetails?.contractor_id, // need to make dynamic
        status_id: statusId,
        temperature: workDiaryDetails?.temperature,
        weather_rating: workDiaryDetails?.weather_rating,
        description: workDiaryDetails?.description,
        work_details: workDiaryDetails?._work_details,
        diary_id: workDiaryDetails?.id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response?.data
  }

  const updateWorkDiaryStatusMutation = useMutation({
    mutationKey: ["update-task-status"],
    mutationFn: (statusId: number) => updateWorkDiaryStatus(statusId),
    onSuccess: (data: DictType) => {
      toast({
        title: translate("Success"),
        description: `${data.number} ${translate("diary updated successfully")}`,
        variant: "success",
      })
      queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  const renderStatusName = (status_id: any) => {
    const statusDetails = lstStatus?.find((obj) => obj.id === status_id)

    return isHebrew
      ? ["", null, undefined].includes(statusDetails?.he)
        ? statusDetails?.en
        : statusDetails?.he
      : statusDetails?.en
  }

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? () => {} : setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='default'
          role='combobox'
          className='h-fit w-fit flex-col items-center whitespace-break-spaces rounded-full py-0 text-[11px] text-black'
          style={{
            background: lstStatus?.find((status: DictType) => status?.id === status_id)?.color,
          }}
        >
          {renderStatusName(status_id)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-0'>
        <Command className={isHebrew ? "rtl" : "ltr"}>
          <CommandInput placeholder={`${translate("Search")}...`} />
          <CommandList>
            <CommandEmpty>{translate("No status found")}.</CommandEmpty>
            <CommandGroup>
              {lstStatus?.map((status: DictType) => (
                <CommandItem
                  key={status.id}
                  value={status?.name}
                  onSelect={(currentValue: any) => {
                    if (disabled) return
                    setOpen(false)
                    updateWorkDiaryStatusMutation.mutate(status?.id)
                  }}
                  className={cn(
                    "my-1 text-black",
                    status?.color === "#transparent" && "bg-primary",
                  )}
                  style={{ background: status?.color }}
                >
                  {status.en}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default WorkDiaryStatusEditor
